import { useEffect } from "react";
import { Box, Button, HStack, Image, Input, Link } from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { pick } from "lodash";
import { useMutation } from "@apollo/client";
import * as userSchema from "utils/schemas/user";
import { useCurrentUserId } from "utils/currentUserHooks";
import { useQuery } from "utils/apollo/hooks";
import { APP_NAME } from "utils/config";
import { getFragmentData, gql } from "__generated__/gql";
import { UserPatch } from "__generated__/gql/graphql";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import {
  getPostgraphileErrorMatchersForFields,
  makePostgraphileErrorProcessor,
} from "utils/PostgraphileErrorExtensions";
import FormFieldWrapper from "components/Shared/Forms/FormFieldWrapper";

const userFragment = gql(/* GraphQL */ `
  fragment UserProfileFields on User {
    id
    firstName
    lastName
    email
    image200: image(size: 200)
  }
`);

const query = gql(/* GraphQL */ `
  query UserProfile {
    me {
      ...UserProfileFields
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation UpdateUserProfile($userInput: UpdateUserInput!) {
    updateUser(input: $userInput) {
      user {
        ...UserProfileFields
        ...CurrentUserFields
      }
    }
  }
`);

const USER_FIELDS = ["firstName", "lastName", "email"] as const;

type Inputs = NonNullable<
  Required<Pick<UserPatch, (typeof USER_FIELDS)[number]>>
>;

const schema: yup.ObjectSchema<Inputs> = yup.object().shape({
  firstName: userSchema.firstName,
  lastName: userSchema.lastName,
  email: userSchema.email,
});

const { FormField } = createFormFieldComponents<Inputs>({ schema });

const processApolloError = makePostgraphileErrorProcessor<Inputs>(
  getPostgraphileErrorMatchersForFields(USER_FIELDS, { type: "User" }),
);

export default function UserProfileForm() {
  const formContext = useForm<Inputs>({
    resolver: yupResolver(schema),
  });
  const {
    formState: { isDirty },
    reset,
  } = formContext;

  const userId = useCurrentUserId();
  const { data, loading: isLoadingData } = useQuery(query);

  const [updateAccount, { loading: isSaving }] = useMutation(mutation);

  const user = getFragmentData(userFragment, data?.me);

  useEffect(() => {
    if (user) {
      reset(pick(user, USER_FIELDS));
    }
  }, [user, reset]);

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    async (formData: Inputs) => {
      if (!userId) return;
      return updateAccount({
        variables: {
          userInput: { id: userId, patch: formData },
        },
      });
    },
    {
      successMessage: "User profile updated successfully.",
      processApolloError,
    },
  );

  const isLoaded = !!userId && !isLoadingData;

  return (
    <Form
      formContext={formContext}
      isLoaded={isLoaded}
      onValidSubmit={handleValidSubmit}
    >
      <HStack my={-3}>
        <FormField name="firstName" autoComplete="given-name" />
        <FormField name="lastName" autoComplete="family-name" />
      </HStack>

      <FormField
        name="email"
        autoComplete="email"
        renderInput={(props) => <Input type="email" {...props} />}
      />

      <FormFieldWrapper
        label="Profile Photo"
        helpText={
          <>
            To set or update your profile photo, create or update an account on{" "}
            <Link href="https://gravatar.com/" isExternal>
              Gravatar <ExternalLinkIcon verticalAlign="text-top" />
            </Link>{" "}
            using your {APP_NAME} account email address
            {user?.email && (
              <>
                {" "}
                (<Link href={`mailto:${user.email}`}>{user.email}</Link>)
              </>
            )}
            .
          </>
        }
        mt={4}
        isLoaded={isLoaded}
        skeletonProps={{ display: "inline-block" }}
      >
        <Image
          src={user?.image200 || undefined}
          boxSize="6rem"
          borderRadius="md"
          alt="User profile picture"
          fallback={
            <Box boxSize="6rem" borderRadius="md" backgroundColor="gray.300" />
          }
        />
      </FormFieldWrapper>

      <Box mt={12} textAlign="right">
        <Button
          type="submit"
          colorScheme="pinkButton"
          size="md"
          disabled={isSaving || !isDirty}
          isLoading={isSaving}
          loadingText="Saving"
        >
          Save
        </Button>
      </Box>
    </Form>
  );
}
